import { featureEnabled, features } from '@sparklayer-io/spark-dash-core/constants/features';
import { createRoute, redirect } from '@tanstack/react-router';
import { loadSessionEnv } from '../../lib/session';
import { HomeIndexRoute, lazyPage } from '../common';
import { siteRoute } from '../layouts';

const pages = () => import('./pages');
const activityComp = lazyPage(pages, 'ActivityHost');
const featuresComp = lazyPage(pages, 'FeaturesHost');
const salesAgentsComp = lazyPage(pages, 'SalesAgentsHost');

// Index of the site-env layout: the platform-chosen home page at /$siteId/$siteEnv.
export const teamOverviewRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '/',
  component: HomeIndexRoute,
});

const activityRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'activity',
  component: activityComp,
  staticData: { title: 'Activity' },
});

const featuresRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'features',
  component: featuresComp,
  staticData: { title: 'Explore Features' },
});

// v2-enabled stores are sent to /v2/customers/agents; everyone else stays on the v1 page.
const salesAgentsRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'sales-agents',
  component: salesAgentsComp,
  staticData: { title: 'Sales Agents' },
  async beforeLoad({ params }) {
    const env = await loadSessionEnv(params.siteId, params.siteEnv, 'partner');
    if (!env) return;
    const platform = env.platform ?? env.connected_platform;
    if (featureEnabled(features.CUSTOMERS_V2, params.siteId, platform)) {
      throw redirect({ to: '/$siteId/$siteEnv/v2/customers/agents', params });
    }
  },
});

// Legacy Orders URL - /$siteId/$siteEnv/orders -> /$siteId/$siteEnv/activity
const ordersAliasRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'orders',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/activity', params });
  },
});

// Legacy Discounts URL - /$siteId/$siteEnv/discounts -> /$siteId/$siteEnv/pricing/discounts
const discountsAliasRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: 'discounts',
  beforeLoad({ params }) {
    throw redirect({ to: '/$siteId/$siteEnv/pricing/discounts', params });
  },
});

// Anything under a site that isn't one of its pages. The session guards look the site up quietly
// (a mistyped url shouldn't raise an error toast) and `/` then lands the user on it - or back home
// when there is no such site.
const unknownSitePageRoute = createRoute({
  getParentRoute: () => siteRoute,
  path: '$',
  staticData: { unknownSitePage: true },
  beforeLoad() {
    throw redirect({ to: '/' });
  },
});

/** Site-env home leaves: activity, features, sales-agents, the legacy aliases and the catch-all. */
export const siteLeaves = [
  activityRoute,
  featuresRoute,
  salesAgentsRoute,
  ordersAliasRoute,
  discountsAliasRoute,
  unknownSitePageRoute,
];
